import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { apiSportCategory } from "../../../src/services/api/Sports";
import PageHeader from "../Header";
interface SportItem {
  id: number;
  name: string;
  slug: string;
  icon: string;
  description: string;
  is_active: number;
  letter?: string;
}

interface HeadingItem {
  id: number;
  name: string;
  slug: string;
  paragraph: string;
  description: string;
  background_image: string;
}

type GroupedData = {
  [key: string]: SportItem[];
};

const SportCategoryPage: React.FC = () => {
  const { slug } = useParams();
  const navigate = useNavigate();

  const [data, setData] = useState<GroupedData>({});
  const [headingData, setHeadingData] = useState<HeadingItem | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSportCategory = async () => {
      try {
        setLoading(true);

        const response = await apiSportCategory(`sport-category/${slug}`);

        setData(response?.data?.data || {});
        setHeadingData(response?.data?.heading_parent || null);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      fetchSportCategory();
    }
  }, [slug]);

  /* 🔥 FLATTEN */
  const flatList = useMemo(() => {
    const list: SportItem[] = [];

    Object.keys(data).forEach((letter) => {
      data[letter].forEach((item, index) => {
        list.push({
          ...item,
          letter: index === 0 ? letter : undefined,
        });
      });
    });

    return list;
  }, [data]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-[60vh]">
        <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader
        title={headingData?.name || slug?.replace(/-/g, " ")}
        slug={headingData?.slug || slug}
        paragraph={headingData?.paragraph}
      />
      {/* GRID */}
      <div className="max-w-7xl mx-auto py-14 px-4 sm:px-6 md:px-8">
        <div className="grid grid-cols-2 gap-4 sm:gap-6 md:grid-cols-3 lg:grid-cols-4">
          {flatList.map((item) => (
            <div key={item.id} className="relative group">
              {/* LETTER BADGE */}
              {item.letter && (
                <div
                  className="
                    absolute -top-3 left-4 z-20
                    bg-white text-blue-900
                    shadow-lg border border-blue-100
                    w-9 h-9 rounded-full
                    flex items-center justify-center
                    font-bold text-sm
                  "
                >
                  {item.letter}
                </div>
              )}

              {/* CARD */}
              <div
                onClick={() =>
                  navigate(`/sport-documents/${item.slug}`, {
                    state: { fromCategory: slug },
                  })
                }
                className="
    relative overflow-hidden cursor-pointer
    rounded-xl bg-white
    border border-gray-200
    shadow-md hover:shadow-2xl
    transition-all duration-500
    hover:-translate-y-2
h-[220px] sm:h-[260px] md:h-[320px]

    flex flex-col
    group
  "
              >
                {/* IMAGE */}
                <div className="relative z-10 flex-1 flex items-center justify-center overflow-hidden">
                  <div
                    className="
        w-full h-full
        overflow-hidden
        bg-gray-100
        transition-all duration-500
      "
                  >
                    <img
                      src={item.icon}
                      alt={item.name}
                      loading="lazy"
                      className="
          w-full h-full object-contain
          p-1
          transition-transform duration-500 ease-out
          group-hover:scale-110
        "
                    />
                  </div>
                </div>

                {/* TITLE */}
                <div className="relative z-10 p-4 border-t-2 border-gray-200">
                  <div
                    className="
        rounded-xl
        px-4 py-3
        bg-gradient-to-r from-blue-900 via-indigo-900 to-blue-700
        transition-all duration-500
        group-hover:scale-105
      "
                  >
                    <h3
                      className="
          text-white
          text-center
          font-bold
          uppercase
          tracking-wide
          line-clamp-2
          transition-all duration-500
          group-hover:tracking-widest
        "
                    >
                      {item.name}
                    </h3>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* EMPTY */}
        {!flatList.length && (
          <div className="text-center py-20">
            <h2 className="text-2xl font-bold text-gray-700">No Data Found</h2>

            <p className="text-gray-500 mt-2">
              No sports categories available.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SportCategoryPage;
